import { useState, useCallback, useEffect, useRef } from 'react';
import { LocationState, STORAGE_KEYS, Coordinates, Permission } from './types';
import { getFromStorage, setToStorage } from './storage-utils';
import { checkBrowserPermission, hasValidCoordinates } from './permission-utils';
import { createLogger } from "@/utils/logger";

const logger = createLogger('location:core');

const SESSION_INITIALIZATION_KEY = 'location_initialized_session';

/**
 * Core hook that manages location state and storage
 */
export const useLocationCore = () => {
  // Track initialization to prevent bouncing
  const initializedRef = useRef<boolean>(false);
  const initializingRef = useRef<boolean>(false);
  const stateUpdateRef = useRef<NodeJS.Timeout | null>(null);
  const lastStateUpdateTimeRef = useRef<number>(0);
  
  // Initialize state from storage with improved initialization
  const [locationState, setLocationState] = useState<LocationState>(() => {
    const savedPermission = getFromStorage<Permission>(STORAGE_KEYS.PERMISSION);
    const savedLocation = getFromStorage<any>(STORAGE_KEYS.COORDS, JSON.parse);
    const locationTimestamp = getFromStorage<number>(STORAGE_KEYS.TIMESTAMP, Number);
    
    // Set session storage flag to track initialization across renders
    const hasInitializedThisSession = sessionStorage.getItem(SESSION_INITIALIZATION_KEY);
    
    if (!hasInitializedThisSession) {
      logger.debug('First initialization this session');
    } else {
      logger.debug('Already initialized this session');
    }
    
    return {
      coords: savedLocation && hasValidCoordinates(savedLocation) ? savedLocation : null,
      // Ensure permission is a valid Permission type with a fallback
      permission: (savedPermission as Permission) || 'prompt',
      isLoading: !hasInitializedThisSession, // Only show loading on first load of the session
      error: null,
      permissionTransitioning: !hasInitializedThisSession, // Same for transition state
      lastUpdated: locationTimestamp || null,
      initialized: !!hasInitializedThisSession, // Track initialization status
    };
  });

  // Safely update state with increased debouncing to prevent UI thrashing
  const safeSetLocationState = useCallback((updater: React.SetStateAction<LocationState>) => {
    // Clear any pending updates
    if (stateUpdateRef.current) {
      clearTimeout(stateUpdateRef.current);
      stateUpdateRef.current = null;
    }
    
    // Throttle updates - don't allow more frequent than every 250ms
    const now = Date.now();
    const timeSinceLastUpdate = now - lastStateUpdateTimeRef.current;
    
    // Minimum wait between updates (throttling)
    const minWait = timeSinceLastUpdate < 250 ? 250 - timeSinceLastUpdate : 0;
    
    // Schedule the update with increased delay (250ms instead of 50ms)
    stateUpdateRef.current = setTimeout(() => {
      setLocationState((prevState) => {
        // Apply the updater (either function or object)
        const newState = typeof updater === 'function' 
          ? updater(prevState) 
          : { ...prevState, ...updater };
        
        // Apply stabilization logic to prevent rapid permission toggling
        // Prefer keeping granted permission state unless definitely changed
        if (prevState.permission === 'granted' && newState.permission === 'prompt' && !newState.permissionTransitioning) {
          // Only switch from granted to other states if we're very sure
          // This prevents bouncing between states
          logger.debug('Attempting to switch from granted to prompt permission, stabilizing...');
          return { 
            ...newState, 
            permission: prevState.permission // Keep the previous 'granted' permission
          };
        }
        
        return newState;
      });
      
      lastStateUpdateTimeRef.current = Date.now();
      stateUpdateRef.current = null;
    }, 250 + minWait); // Increased base delay from 50ms to 250ms
  }, []);

  // Initialize by checking actual browser permission on mount
  useEffect(() => {
    if (initializedRef.current || initializingRef.current) {
      logger.debug('Skipping initialization as it has already run');
      return;
    }

    const initializePermissionState = async () => {
      initializingRef.current = true;
      
      try {
        // Mark this session as having attempted initialization
        sessionStorage.setItem(SESSION_INITIALIZATION_KEY, 'true');
        
        // Get actual browser permission
        logger.debug('Initializing permission state');
        const browserPermission = await checkBrowserPermission();
        
        // Log the comparison between stored and actual permission
        if (locationState.permission !== browserPermission) {
          logger.debug(`Stored permission (${locationState.permission}) differs from browser permission (${browserPermission}). Updating to match browser.`);
        }

        // Handle state inconsistencies
        if (locationState.coords && browserPermission !== 'granted') {
          logger.debug('Inconsistent state: Have coordinates but browser permission is not granted. Clearing coordinates.');
          setToStorage(STORAGE_KEYS.COORDS, null);
          setToStorage(STORAGE_KEYS.TIMESTAMP, null);
          
          safeSetLocationState(prev => ({
            ...prev,
            permission: browserPermission,
            coords: null,
            lastUpdated: null,
            isLoading: false,
            permissionTransitioning: false,
            initialized: true
          }));
        } else {
          // Normal flow - update state with actual browser permission
          safeSetLocationState(prev => ({
            ...prev,
            permission: browserPermission,
            isLoading: false,
            permissionTransitioning: false,
            initialized: true
          }));
        }
        
        // Update storage to match browser - only if actually different
        if (getFromStorage(STORAGE_KEYS.PERMISSION) !== browserPermission) {
          setToStorage(STORAGE_KEYS.PERMISSION, browserPermission);
        }
        initializedRef.current = true;
      } catch (err) {
        logger.warn('Error initializing permission state:', err);
        // Set to default safe values on error
        safeSetLocationState(prev => ({
          ...prev,
          isLoading: false,
          permissionTransitioning: false,
          initialized: true
        }));
      } finally {
        initializingRef.current = false;
      }
    };
    
    // Run initialization asynchronously
    const initPromise = initializePermissionState();
    Promise.resolve(initPromise).catch(err => {
      logger.error('Unhandled error during initialization:', err);
      initializingRef.current = false;
    });
    
    // No dependencies to avoid re-initialization
  }, [locationState.permission, locationState.coords, safeSetLocationState]);

  // Save location with reduced storage operations and improved stability
  const saveLocation = useCallback((coords: Coordinates, syncWithProfile = true) => {
    if (!hasValidCoordinates(coords)) {
      logger.warn('Attempted to save invalid coordinates:', coords);
      return null;
    }
    
    const timestamp = Date.now();
    
    // Only update storage when values change
    if (!locationState.coords || 
        locationState.coords.lat !== coords.lat || 
        locationState.coords.lng !== coords.lng) {
      setToStorage(STORAGE_KEYS.COORDS, coords);
      setToStorage(STORAGE_KEYS.TIMESTAMP, timestamp);
    }
    
    // Only update the permission if it's not already granted
    // This prevents unnecessary permission state bouncing
    if (locationState.permission !== 'granted') {
      setToStorage(STORAGE_KEYS.PERMISSION, 'granted');
      logger.debug('Updated permission to granted after successful location update');
    }
    
    safeSetLocationState(prev => ({
      ...prev,
      coords,
      permission: 'granted' as Permission,
      lastUpdated: timestamp,
      error: null,
      isLoading: false,
      permissionTransitioning: false
    }));
    
    logger.debug(`Location updated: (${coords.lat}, ${coords.lng})`);
    
    return {
      coords,
      timestamp
    };
  }, [locationState.coords, locationState.permission, safeSetLocationState]);

  // Clear location data
  const clearLocation = useCallback(() => {
    setToStorage(STORAGE_KEYS.COORDS, null);
    setToStorage(STORAGE_KEYS.TIMESTAMP, null);
    
    safeSetLocationState(prev => ({
      ...prev,
      coords: null,
      lastUpdated: null
    }));
  }, [safeSetLocationState]);

  // Check if location is stale
  const isLocationStale = useCallback((staleTolerance: number) => {
    if (!locationState.lastUpdated) return true;
    const now = Date.now();
    const ageInMinutes = (now - locationState.lastUpdated) / (60 * 1000);
    return ageInMinutes > staleTolerance;
  }, [locationState.lastUpdated]);

  return {
    locationState,
    setLocationState: safeSetLocationState,
    saveLocation,
    clearLocation,
    isLocationStale,
    setToStorage
  };
};
